/**
 * SubscribeButton component for quick subscription checkout.
 * 
 * This component provides a streamlined one-click subscription experience
 * that takes users directly to Stripe checkout for the PRO plan.
 */

import React, { useState, useEffect } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { createCheckoutSession, fetchSubscriptionPlans } from '../utils/api';
import analytics from '../services/analytics';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function SubscribeButton({ 
  variant = 'contained', 
  size = 'medium',
  fullWidth = false,
  sx = {},
  buttonText = null,
  onError = null,
  color = 'primary',
  isAlphaTester = false
}) {
  const [loading, setLoading] = useState(false);
  const [planId, setPlanId] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);
  
  // Default button text with price
  const defaultButtonText = 'Subscribe for $16.99/month';
  const displayText = buttonText || defaultButtonText;
  
  // Fetch the plan ID on mount
  useEffect(() => {
    fetchSubscriptionPlans()
      .then(res => {
        // Find the PRO/premium plan
        const proPlan = res.data.find(plan => 
          plan.plan_type === 'premium' || 
          plan.plan_name.toLowerCase().includes('pro')
        );
        if (proPlan) {
          setPlanId(proPlan.plan_id);
          setPlanDetails(proPlan);
        } else {
          // Try to find any plan if no PRO plan exists
          if (res.data.length > 0) {
            setPlanId(res.data[0].plan_id);
            setPlanDetails(res.data[0]);
          }
        }
      })
      .catch(err => {
        console.error('Failed to fetch subscription plans:', err);
        if (onError) {
          onError('Failed to load subscription plans');
        }
      });
  }, [onError]);

  const handleSubscribe = async () => {
    if (!planId || !planDetails) {
      if (onError) {
        onError('Subscription plan not available. Please try again.');
      }
      return;
    }
    
    try {
      setLoading(true);
      
      // Track subscription checkout started
      analytics.trackSubscriptionCheckoutStarted(
        planId,
        planDetails.plan_name,
        planDetails.price_cents,
        {
          exam_allowance: planDetails.exam_allowance,
          is_alpha_tester: isAlphaTester,
          source: 'subscribe_button',
          page: window.location.pathname
        }
      );
      
      // Create checkout session (backend handles founder's deal automatically)
      const response = await createCheckoutSession(
        planId,
        `${window.location.origin}/subscription/success`,
        window.location.href // Return to current page if cancelled
      );
      
      // Redirect to Stripe checkout
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.session_id
      });
      
      if (error) {
        console.error('Stripe redirect error:', error);
        
        // Track checkout abandoned
        analytics.trackSubscriptionCheckoutAbandoned(
          planId,
          planDetails.plan_name,
          {
            error_message: error.message,
            error_type: 'stripe_redirect_error',
            source: 'subscribe_button'
          }
        );
        
        if (onError) {
          onError(error.message);
        }
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      
      // Track checkout abandoned
      analytics.trackSubscriptionCheckoutAbandoned(
        planId,
        planDetails.plan_name,
        {
          error_message: err.response?.data?.detail || err.message,
          error_type: 'api_error',
          source: 'subscribe_button'
        }
      );
      
      if (onError) {
        onError(err.response?.data?.detail || 'Failed to start checkout process');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      color={color}
      fullWidth={fullWidth}
      onClick={handleSubscribe}
      disabled={loading || !planId}
      sx={{
        textTransform: 'none', // Prevent uppercase transformation
        fontWeight: 600,
        // Add extra styling for large buttons
        ...(size === 'large' && {
          fontSize: '1.125rem',
          padding: '12px 32px',
        }),
        ...sx
      }}
    >
      {loading ? (
        <>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          Processing...
        </>
      ) : (
        displayText
      )}
    </Button>
  );
}

export default SubscribeButton;